import axios from 'axios';
import type { Task, Board, Sprint, Team, User, Label, Comment, Attachment, SubTask, BoardColumn, TaskStatus } from '../types';

const api = axios.create({
    baseURL: 'http://localhost:3003/tm',
    headers: { 'Content-Type': 'application/json' },
});

// Interceptor to inject auth token if present
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers['x-access-token'] = token;
    }
    return config;
});

// Generic response handler
function handleResponse<T>(res: { data: { success: boolean; data: T; error?: string } }): T {
    if (!res.data.success) throw new Error(res.data.error || 'API Error');
    return res.data.data;
}

// =====================================================================
// Labels
// =====================================================================
export async function fetchLabels(): Promise<Label[]> {
    const res = await api.get('/labels');
    return handleResponse(res);
}

export async function createLabel(label: Omit<Label, 'id'>): Promise<Label> {
    const res = await api.post('/labels', label);
    return handleResponse(res);
}

export async function updateLabel(label: Label): Promise<boolean> {
    const res = await api.put('/labels', { id: label.id, name: label.name, color: label.color });
    return handleResponse(res);
}

export async function deleteLabel(id: string): Promise<boolean> {
    const res = await api.delete('/labels', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// Users
// =====================================================================
export async function fetchUsers(): Promise<User[]> {
    const res = await api.get('/users');
    return handleResponse(res);
}

export async function updateUserRole(userId: string, role: string): Promise<boolean> {
    const res = await api.put('/users/role', { userId, role });
    return handleResponse(res);
}

export async function updateUserAvatar(userId: string, avatarColor: string): Promise<boolean> {
    const res = await api.put('/users/avatar', { userId, avatarColor });
    return handleResponse(res);
}

// =====================================================================
// Teams
// =====================================================================
export async function fetchTeams(): Promise<Team[]> {
    const res = await api.get('/teams');
    return handleResponse(res);
}

export async function createTeam(team: Omit<Team, 'id' | 'createdAt'>): Promise<Team> {
    const res = await api.post('/teams', team);
    return handleResponse<{ data: Team }>(res) as unknown as Team;
}

export async function updateTeam(team: Team): Promise<Team> {
    const { id, ...rest } = team;
    const res = await api.put('/teams', { id, ...rest });
    return handleResponse<{ data: Team }>(res) as unknown as Team;
}

export async function deleteTeam(id: string): Promise<boolean> {
    const res = await api.delete('/teams', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// Boards
// =====================================================================
export async function fetchBoards(): Promise<Board[]> {
    const res = await api.get('/boards');
    return handleResponse(res);
}

export async function createBoard(board: Omit<Board, 'id' | 'createdAt'>): Promise<Board> {
    const res = await api.post('/boards', board);
    return handleResponse<{ data: Board }>(res) as unknown as Board;
}

export async function updateBoard(board: Partial<Board> & { id: string }): Promise<Board> {
    const res = await api.put('/boards', board);
    return handleResponse<{ data: Board }>(res) as unknown as Board;
}

export async function deleteBoard(id: string): Promise<boolean> {
    const res = await api.delete('/boards', { data: { id } });
    return handleResponse(res);
}

export async function updateBoardColumns(boardId: string, columns: BoardColumn[]): Promise<Board> {
    const res = await api.put('/boards/columns', { boardId, columns });
    return handleResponse<{ data: Board }>(res) as unknown as Board;
}

// =====================================================================
// Sprints
// =====================================================================
export async function fetchSprints(boardId?: string): Promise<Sprint[]> {
    const res = await api.get('/sprints', { params: boardId ? { boardId } : {} });
    return handleResponse(res);
}

export async function createSprint(sprint: Omit<Sprint, 'id'>): Promise<Sprint> {
    const res = await api.post('/sprints', sprint);
    return handleResponse<{ data: Sprint }>(res) as unknown as Sprint;
}

export async function updateSprint(sprint: Partial<Sprint> & { id: string }): Promise<Sprint> {
    const res = await api.put('/sprints', sprint);
    return handleResponse<{ data: Sprint }>(res) as unknown as Sprint;
}

export async function deleteSprint(id: string): Promise<boolean> {
    const res = await api.delete('/sprints', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// Tasks
// =====================================================================
export async function fetchTasks(params?: { boardId?: string; sprintId?: string; status?: string; assigneeId?: string; search?: string }): Promise<Task[]> {
    const res = await api.get('/tasks', { params });
    return handleResponse(res);
}

export async function fetchTask(guid: string): Promise<Task> {
    const res = await api.get(`/tasks/${guid}`);
    return handleResponse(res);
}

export async function createTask(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<Task> {
    const res = await api.post('/tasks', task);
    return handleResponse<{ data: Task }>(res) as unknown as Task;
}

export async function updateTask(task: Partial<Task> & { id: string }): Promise<Task> {
    const res = await api.put('/tasks', task);
    return handleResponse<{ data: Task }>(res) as unknown as Task;
}

export async function deleteTask(id: string): Promise<boolean> {
    const res = await api.delete('/tasks', { data: { id } });
    return handleResponse(res);
}

export async function moveTask(taskId: string, status: TaskStatus): Promise<boolean> {
    const res = await api.put('/tasks/move', { taskId, status });
    return handleResponse(res);
}

// Comments
export async function addComment(taskId: string, content: string, attachments?: { name: string; size: number; type: string; url: string }[]): Promise<Comment> {
    const res = await api.post('/tasks/comment', { taskId, content, attachments });
    return handleResponse(res);
}

// Attachments
export async function addAttachment(taskId: string, attachment: { name: string; size: number; type: string; url: string }): Promise<Attachment> {
    const res = await api.post('/tasks/attachment', { taskId, ...attachment });
    return handleResponse(res);
}

export async function removeAttachment(id: string): Promise<boolean> {
    const res = await api.delete('/tasks/attachment', { data: { id } });
    return handleResponse(res);
}

// Subtasks
export async function addSubtask(taskId: string, subtask: Omit<SubTask, 'id'>): Promise<SubTask> {
    const res = await api.post('/tasks/subtask', { taskId, ...subtask });
    return handleResponse(res);
}

export async function updateSubtask(subtask: Partial<SubTask> & { id: string }): Promise<boolean> {
    const res = await api.put('/tasks/subtask', subtask);
    return handleResponse(res);
}

export async function deleteSubtask(id: string): Promise<boolean> {
    const res = await api.delete('/tasks/subtask', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// File uploads (via UploadController)
// =====================================================================
export async function uploadFile(file: File): Promise<{ path: string; id_upload: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post('http://localhost:3003/upload/file', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-access-token': localStorage.getItem('token') || '',
        },
    });
    if (!res.data.success) throw new Error(res.data.error || 'Upload failed');
    return res.data.data;
}

// Assign / unassign a task to a sprint
export async function assignTaskToSprint(taskId: string, sprintId: string | null): Promise<boolean> {
    const res = await api.put('/tasks', { id: taskId, sprintId });
    if (!res.data.success) throw new Error(res.data.error || 'API Error');
    return true;
}

export default api;
